import React, { memo } from 'react';
import { Stack, Typography, useTheme, styled } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import WFHLogo from 'assets/WFHLogo';
import PropTypes from 'prop-types';
import { hasValue } from 'swipeclock-frontend';

/**
 * PoweredByFooter component displays a footer with branding information.
 * Shows either white label client branding or default Swipeclock branding.
 * Hidden on mobile breakpoints and in enclosed mode.
 *
 * @param {Object} props - Component props
 * @param {Object} props.whiteLabelState - White label configuration object
 * @param {string} [props.whiteLabelState.labelProvidedBy] - Custom label text for white label clients
 * @param {boolean} [props.whiteLabelState.hasWhiteLabelClient] - Whether this is a white label client
 * @param {boolean} [props.whiteLabelState.isEnclosedMode] - Whether the component is in enclosed mode
 * @param {string} [props.whiteLabelState.labelColor] - Custom color for the label text
 * @param {...Object} [props] - Additional props passed to the footer container
 * @returns {JSX.Element|null} The rendered footer component or null if hidden
 */
function PoweredByFooter({ whiteLabelState, ...props }) {
  const { isEnclosedMode, labelColor } = whiteLabelState || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));
  const size = 32;

  // Don't render on mobile breakpoint
  if (isMobile || isEnclosedMode) {
    return null;
  }

  // Commenting instead of deleting just in case we decide to add custom labels
  // in the near future. labelProvidedBy and hasWhiteLabelClient come from
  // whiteLabelState.
  // if (hasWhiteLabelClient && !isEnclosedMode && hasValue(labelProvidedBy)) {
  //   return (
  //     <StyledFooter {...props}>
  //       <StyledTypography colorTwo={labelColor}>
  //         Powered by {labelProvidedBy}
  //       </StyledTypography>
  //     </StyledFooter>
  //   );
  // }

  return (
    <StyledFooter {...props}>
      <WFHLogo
        color={hasValue(labelColor) ? labelColor : theme.palette.common.white}
        opacity={1}
        width={size}
        height={size}
      />
      <StyledTypography colorTwo={labelColor}>
        Powered by Swipeclock
      </StyledTypography>
    </StyledFooter>
  );
}

export default memo(PoweredByFooter);

PoweredByFooter.propTypes = {
  whiteLabelState: PropTypes.shape({
    labelProvidedBy: PropTypes.string,
    hasWhiteLabelClient: PropTypes.bool,
    isEnclosedMode: PropTypes.bool,
    labelColor: PropTypes.string,
  }),
};

/** styled components */

const StyledFooter = styled((props) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    gap={1}
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const StyledTypography = styled(
  (props) => <Typography variant="body2" {...props} />,
  { shouldForwardProp: (prop) => prop !== 'colorTwo' }
)(({ theme, colorTwo }) => ({
  color: hasValue(colorTwo) ? colorTwo : theme.palette.common.white,
}));
